// Dependencies
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { escape } from 'lodash'
import { navigate } from 'gatsby'
import Cookies from 'js-cookie'

// Components
import { Input, InputOption } from './inputs'
import { Modal } from 'components/Modal'
import { LeadForm } from 'components/LeadForm'
import { Button } from 'component-library/button'
import { Asset } from 'components/utilities/asset'
import { Icon } from 'component-library/icon'
import { NavbarButton } from 'component-library/navbar-button'

// Styles
import * as S from './styles'

// Data
import data from './data'

// Utils
import { setLocalStorageResult } from 'utils/results'
import { USER_LOCAL_STORAGE } from 'utils/constants'

// Services
import { setLocalStorage } from 'services/local-storage'
import getStates from 'services/get-states'
import {
  getCursosByUF,
  getModalidadesByCursoAndUF,
  getValuesByModalidade
} from 'services/pro_sisu_Api'
import { leadsApi } from '../../services/leads-api'

// Assets
import close from 'assets/close.svg'

// Types
type FormProps = {
  linguagens: string
  humanas: string
  matematica: string
  natureza: string
  redacao: string
  curso: string
  uf: string
  modalidade: string
}

interface IProps {
  paramsSisu?: { type: string; value: number }[]
}

const SimulatorSisu = ({ paramsSisu = [] }: IProps) => {
  const { register, handleSubmit, setValue, getValues } = useForm<FormProps>()
  const [active, setactive] = useState<string>('')
  const [ufs, setufs] = useState<{ nome: string; uf: string }[]>([])
  const [courses, setcourses] = useState<any[]>([])
  const [modalidades, setmodalidades] = useState<any[]>([])
  const [currentUF, setcurrentUF] = useState<string>('')
  const [showMessage, setshowMessage] = useState<string | null>(null)
  const [loading, setloading] = useState(false)
  const [isModalOpened, setIsModalOpened] = useState(false)

  useEffect(() => {
    paramsSisu.forEach((param) => {
      let name = param.type
      if (name === 'exatas') name = 'matematica'

      setValue(name as any, param.value)
    })
  }, [paramsSisu])

  function resetStates() {
    setcourses([])
    setcurrentUF('')
    setmodalidades([])
  }

  const getDatas = useCallback(async () => {
    const resp = await getStates()
    if (resp.length > 0) setufs(resp)
  }, [])

  useEffect(() => {
    getDatas()
  }, [])

  async function getCursos(e: any) {
    resetStates()
    setloading(true)
    const uf_name = e.target.value

    try {
      const resp = await getCursosByUF(uf_name)
      if (resp.length > 0) {
        setcurrentUF(uf_name)
        setcourses(resp)
      }
    } catch (err: any) {
      console.error(err)
      onFailure('Não há cursos cadastrados para este estado')
      resetStates()
    } finally {
      setloading(false)
    }
  }

  async function getModalidades(e: any) {
    setmodalidades([])
    const name_course = escape(e.target.value)
    setloading(true)

    try {
      const resp = await getModalidadesByCursoAndUF(currentUF, name_course)
      setmodalidades(resp)
    } catch (error) {
      console.error(error)
      onFailure('Erro ao recuperar modalidades deste estado!')
      resetStates()
    } finally {
      setloading(false)
    }
  }

  function setModalidade() {}

  const InputDefault = (subject: any, className: string) => {
    const localClassName = className.toLocaleLowerCase()
    function onToggleActive(e: any) {
      if (e.keyCode === 13 || e.keyCode === 9) {
        setactive(localClassName)
      }
    }

    const defaultValue = paramsSisu.find(
      (param) =>
        param.type === localClassName ||
        (param.type === 'exatas' && localClassName === 'matematica')
    )?.value

    return (
      <div className={'inputs'} onClick={() => setactive(localClassName)}>
        <Input
          changeFunction={onToggleActive}
          ref={register}
          isActive={active === localClassName}
          subject={subject}
          className={className}
          name={localClassName}
          defaultValue={defaultValue}
        />
      </div>
    )
  }

  const SelectOption = (
    label: any,
    className: string,
    values: any[],
    changeFunc: Function,
    isDisabled: boolean
  ) => {
    function toActive() {
      setactive(className.toLocaleLowerCase())
    }

    return (
      <InputOption
        clickFunction={toActive}
        changeFunction={changeFunc}
        label={label}
        values={values}
        register={register}
        isDisabled={!!isDisabled}
        isActive={active === className.toLocaleLowerCase() ? true : false}
        className={className}
        name={className.toLocaleLowerCase()}
      />
    )
  }

  const getTokenCookie = () => Cookies.get('authData')

  const createLeadsTag = async () => {
    try {
      const tag = process.env.REACT_LEADS_TAG_VALUE

      await leadsApi.post(`/me/leads`, { tag })

      return true
    } catch (error: any) {
      console.error('Error creating leads tag:', error)
      return false
    }
  }

  const handleSubmitSisu = (body: any) => {
    try {
      if (!body) {
        return
      }

      let { redacao } = body
      if (parseFloat(redacao) <= 0) throw new Error()

      if (!getTokenCookie()) {
        setIsModalOpened(true)
      } else {
        createLeadsTag()
        onSubmitForm(body)
      }
    } catch (err: any) {
      console.error(err)
      onFailure('Redação precisa ser maior do que zero!')
    }
  }

  const onSubmitForm = useCallback((body: any) => {
    calculate(body)
  }, [])

  const calculate = async (body: FormProps) => {
    try {
      const response = await getValuesByModalidade(body)

      if (!response?.length) {
        throw new Error()
      }

      setLocalStorageResult({ formValues: body, results: response })

      const searchParams = window.location.search
      navigate(`/resultados${searchParams}`)
    } catch (err: any) {
      console.error(err)
      onFailure('Não foi possível calcular')
    }
  }

  const onFailure = (message: string) => {
    setIsModalOpened(false)
    setshowMessage(message)
    setTimeout(() => {
      setshowMessage(null)
    }, 5000)
  }

  return (
    <S.Background>
      <Modal
        isOpened={isModalOpened}
        handleOutsideClick={() => {
          setIsModalOpened(false)
        }}
        content={
          <LeadForm
            title="INSCRIÇÃO"
            subtitle="Acesse agora o seu resultado"
            closeIconElement={
              <Asset
                source={close}
                size={18}
                color="#3A3E47"
                hoverColor="#2c2f36"
                handleClick={() => setIsModalOpened(false)}
              />
            }
            cancelButtonElement={
              <Button
                label="Cancelar"
                type="button"
                variant="dark"
                onClick={() => setIsModalOpened(false)}
              />
            }
            onSubmit={(data) => {
              setLocalStorage(USER_LOCAL_STORAGE, {
                user: data
              })
              onSubmitForm(getValues())
              setIsModalOpened(false)
            }}
          />
        }
      />

      <S.FormWrapper onSubmit={handleSubmit(handleSubmitSisu)}>
        <S.HeaderItem>
          <Icon size={24} name="pencil" color="#df1d3d" />
          <p>Insira suas notas</p>
        </S.HeaderItem>

        <S.InputsWrapper>
          {InputDefault('Linguagens', 'linguagens')}
          {InputDefault('Humanas', 'humanas')}
          {InputDefault('Natureza', 'natureza')}
          {InputDefault('Matemática', 'matematica')}
          {InputDefault('Redação', 'redacao')}
        </S.InputsWrapper>

        <S.Separator />

        <S.HeaderItem>
          <Icon size={24} name="graduation" color="#df1d3d" />
          <p>{data.labels_options.title}</p>
        </S.HeaderItem>

        <S.SelectsWrapper>
          {SelectOption(
            data.labels_options.label_uf,
            'uf',
            ufs,
            getCursos,
            !ufs.length
          )}
          {SelectOption(
            data.labels_options.label_course,
            'course',
            courses,
            getModalidades,
            !courses.length
          )}
          {SelectOption(
            data.labels_options.label_modalidade,
            'modalidade',
            modalidades,
            setModalidade,
            !modalidades.length
          )}
        </S.SelectsWrapper>

        <NavbarButton
          type="submit"
          label="Simular agora"
          variant="primary"
          borderType="large"
          iconElement={
            <Icon name="arrowsClockwise" size={16} color="#fdf4f5" />
          }
        />
        {loading && <span id="loading">Estamos carregando!</span>}
      </S.FormWrapper>
    </S.Background>
  )
}

export default SimulatorSisu
